import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Calendar, Trash2, Plus, Check, X, Loader2, Users,
  Settings as SettingsIcon, Search, Percent,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import useBulkSelect from "../../lib/useBulkSelect";
import { BulkCheckbox } from "../../components/BulkActionBar";

/**
 * Admin > Promoções
 *
 * Two parallel systems coexist:
 *   1. Global apartment-pricing promo (legacy) — kept as "Promoção de preço"
 *   2. Client-specific voucher promos (new) — welcome / referral / manual
 *
 * Tabs: Configuração · Atribuir · Ativas · Agendadas · Histórico.
 */
const TABS = [
  { key: "config", label: "Configuração", icon: SettingsIcon },
  { key: "approvals", label: "Por aprovar", icon: Users },
  { key: "assign", label: "Atribuir", icon: Plus },
  { key: "active", label: "Ativas", icon: Sparkles },
  { key: "redeemed", label: "Usadas", icon: Check },
  { key: "expired", label: "Expiradas", icon: X },
];

export default function AdminPromotion() {
  const [tab, setTab] = useState("config");

  return (
    <div className="space-y-8" data-testid="admin-promotion-page">
      <div>
        <div className="eyebrow mb-3">Marketing</div>
        <h1 className="font-display text-4xl sm:text-5xl">Promoções</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
          Gira a oferta de boas-vindas automática (5€/30 dias por defeito), recompensas
          por indicação e promoções pontuais atribuídas a clientes específicos.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="promo-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm flex items-center gap-2 transition-all ${
                      active ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]" : "border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37]/50"
                    }`}
                    data-testid={`promo-tab-${t.key}`}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "config" && <ConfigPane />}
      {tab === "approvals" && <ApprovalsPane />}
      {tab === "assign" && <AssignPane />}
      {tab === "active" && <ListPane status="active" />}
      {tab === "redeemed" && <ListPane status="redeemed" />}
      {tab === "expired" && <ListPane status="expired" />}
    </div>
  );
}

// --------------------------------------------------------- Approvals pane

function ApprovalsPane() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/referral-approvals")
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function act(id, action) {
    setBusy(id + action);
    try {
      await api.post(`/admin/referral-approvals/${id}/${action}`);
      toast.success(action === "approve" ? "Aprovada · desconto enviado" : "Rejeitada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  }

  if (loading) return <div className="text-sm text-[#a0b2af]">A carregar…</div>;
  if (items.length === 0) return (
    <div className="lux-card p-10 text-center text-sm text-[#a0b2af]" data-testid="approvals-empty">
      Sem recomendações por aprovar.
    </div>
  );

  return (
    <div className="space-y-3" data-testid="approvals-list">
      {items.map((r) => (
        <div key={r.id} className="lux-card p-5" data-testid={`approval-${r.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-[#d4af37]">Recomendação pendente</div>
              <div className="mt-2 text-sm">
                <strong className="text-[#fdfbf7]">{r.referrer_name}</strong>{" "}
                <span className="text-[#a0b2af]">({r.referrer_reg || r.referrer_email})</span>
                <span className="text-[#a0b2af] mx-2">→</span>
                <strong className="text-[#fdfbf7]">{r.referred_name}</strong>{" "}
                <span className="text-[#a0b2af]">({r.referred_reg || r.referred_email})</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] mt-2">
                Disparado no {r.trigger === "first_booking" ? "1.º pagamento" : "registo"} · {r.created_at?.slice(0, 10)}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => act(r.id, "approve")}
                      disabled={!!busy}
                      className="btn-gold px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`approve-${r.id}`}>
                {busy === r.id + "approve" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Aprovar
              </button>
              <button onClick={() => act(r.id, "reject")}
                      disabled={!!busy}
                      className="px-4 py-2 text-xs uppercase tracking-widest border border-[#ef4444]/60 text-[#fca5a5] hover:bg-[#ef4444]/10 rounded-sm flex items-center gap-1.5 disabled:opacity-50"
                      data-testid={`reject-${r.id}`}>
                <X size={12} /> Rejeitar
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Config

function ConfigPane() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/promo-settings").then(({ data }) => setCfg(data));
  }, []);

  function patch(p) { setCfg((c) => ({ ...c, ...p })); }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/promo-settings", cfg);
      setCfg(data);
      toast.success("Configurações guardadas");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  if (!cfg) return <div className="text-sm text-[#a0b2af]">A carregar…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="config-pane">
      <Section title="Boas-vindas automática" hint="Promoção ativada automaticamente no registo de cada novo cliente.">
        <Toggle label="Ativa" checked={cfg.welcome_enabled}
                onChange={(v) => patch({ welcome_enabled: v })}
                testId="welcome-toggle" />
        <NumField label="Valor (€)" value={cfg.welcome_amount_eur}
                  onChange={(v) => patch({ welcome_amount_eur: v })} testId="welcome-amount" />
        <NumField label="Duração (dias)" value={cfg.welcome_days}
                  onChange={(v) => patch({ welcome_days: v })} testId="welcome-days" />
      </Section>

      <Section title="Recompensa por indicação" hint="Cliente que indica recebe uma promoção automaticamente.">
        <Toggle label="Ativa" checked={cfg.referral_enabled}
                onChange={(v) => patch({ referral_enabled: v })} testId="ref-toggle" />
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mb-1.5">Dispara quando…</label>
          <select value={cfg.referral_trigger}
                  onChange={(e) => patch({ referral_trigger: e.target.value })}
                  className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-sm"
                  data-testid="ref-trigger">
            <option value="admin_approval">Aprovação do admin (recomendado)</option>
            <option value="register">Novo cliente conclui registo</option>
            <option value="first_booking">Novo cliente paga a 1.ª reserva</option>
          </select>
        </div>
        <NumField label="Valor ao recomendador (€)" value={cfg.referral_amount_eur}
                  onChange={(v) => patch({ referral_amount_eur: v })} testId="ref-amount" />
        <NumField label="Duração (dias)" value={cfg.referral_days}
                  onChange={(v) => patch({ referral_days: v })} testId="ref-days" />
      </Section>

      <Section title="Boas-vindas ao cliente indicado (opcional)" hint="Cliente recomendado recebe também um bónus ao registar-se.">
        <Toggle label="Ativa" checked={cfg.welcome_for_referred_enabled}
                onChange={(v) => patch({ welcome_for_referred_enabled: v })}
                testId="referred-toggle" />
        <NumField label="Valor (€)" value={cfg.welcome_for_referred_amount}
                  onChange={(v) => patch({ welcome_for_referred_amount: v })} testId="referred-amount" />
        <NumField label="Duração (dias)" value={cfg.welcome_for_referred_days}
                  onChange={(v) => patch({ welcome_for_referred_days: v })} testId="referred-days" />
      </Section>

      <GalleryVisibilityCard />

      <button onClick={save} disabled={saving}
              className="btn-gold px-6 py-3 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
              data-testid="config-save">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        Guardar configurações
      </button>
    </div>
  );
}

/**
 * Self-contained card to enable/disable the public /galeria page. Lives
 * inside ConfigPane but doesn't share state with `cfg` because it has its
 * own dedicated endpoint.
 */
function GalleryVisibilityCard() {
  const [enabled, setEnabled] = useState(null);  // null = loading
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/gallery/status")
       .then(({ data }) => setEnabled(!!data.enabled))
       .catch(() => setEnabled(true));
  }, []);

  async function toggle(next) {
    setBusy(true);
    try {
      await api.post("/admin/gallery/visibility", { enabled: next });
      setEnabled(next);
      toast.success(next ? "Galeria pública ativada" : "Galeria pública desativada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="lux-card p-5 md:p-6 space-y-3" data-testid="gallery-visibility-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-display text-xl">Galeria pública</div>
          <div className="text-xs text-[#a0b2af] mt-1 max-w-md">
            Quando desativada, a página <span className="font-mono text-[#d4af37]">/galeria</span> e
            o respetivo link no rodapé deixam de aparecer aos visitantes.
            O admin continua a gerir as fotos em <span className="font-mono text-[#d4af37]">/admin/galeria</span>.
          </div>
        </div>
        <button
          onClick={() => toggle(!enabled)}
          disabled={enabled === null || busy}
          className={`px-5 py-2 text-xs uppercase tracking-widest border rounded-sm inline-flex items-center gap-2 transition-colors disabled:opacity-50 ${
            enabled
              ? "border-[#10b981]/60 text-[#10b981] hover:bg-[#10b981]/10"
              : "border-[#a0b2af]/40 text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37]"
          }`}
          data-testid="gallery-visibility-toggle">
          {busy ? <Loader2 size={12} className="animate-spin" /> : null}
          {enabled === null ? "A carregar…"
            : enabled ? "Visível ao público · clicar para ocultar"
                      : "Oculta · clicar para ativar"}
        </button>
      </div>
    </div>
  );
}


function Section({ title, hint, children }) {
  return (
    <div className="lux-card p-5 md:p-6 space-y-4">
      <div>
        <div className="font-display text-xl">{title}</div>
        {hint && <div className="text-xs text-[#a0b2af] mt-1">{hint}</div>}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none"
           data-testid={`toggle-wrap-${testId}`}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
             className="w-5 h-5 accent-[#d4af37]" data-testid={testId} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mb-1.5">{label}</label>
      <input type="number" value={value ?? 0} min="0" step="0.5"
             onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
             className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-sm font-mono"
             data-testid={testId} />
    </div>
  );
}

// ---------------------------------------------------------------- Assign

function AssignPane() {
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState("");
  const [amount, setAmount] = useState(10);
  const [percentage, setPercentage] = useState(0);
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/promos/candidates").then(({ data }) => setCandidates(data.items || []));
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return candidates;
    return candidates.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.email || "").toLowerCase().includes(s) ||
      (c.registration_number || "").toLowerCase().includes(s)
    );
  }, [candidates, search]);

  const ids = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const bulk = useBulkSelect(ids);

  async function assign() {
    if (bulk.count === 0) { toast.error("Selecione ao menos um cliente"); return; }
    if ((amount || 0) === 0 && (percentage || 0) === 0) {
      toast.error("Defina um valor ou percentagem de desconto");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/promos", {
        client_ids: Array.from(bulk.selected),
        amount_eur: amount || 0,
        percentage: percentage || 0,
        days,
        note: note || null,
      });
      toast.success(`${data.created} promoção(ões) criada(s)`);
      bulk.clear();
      setNote("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="space-y-5" data-testid="assign-pane">
      <div className="lux-card p-5 md:p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <NumField label="Valor (€)" value={amount} onChange={setAmount} testId="assign-amount" />
        <NumField label="Percentagem (%)" value={percentage} onChange={setPercentage} testId="assign-pct" />
        <NumField label="Dias válidos" value={days} onChange={setDays} testId="assign-days" />
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mb-1.5">Nota interna</label>
          <input value={note} onChange={(e) => setNote(e.target.value)}
                 placeholder="Ex: cliente fidelizado"
                 className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-sm"
                 data-testid="assign-note" />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0b2af]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Pesquisar cliente por nome, email ou Nº Reg."
                 className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm pl-9 pr-3 py-2 text-sm"
                 data-testid="assign-search" />
        </div>
        <button onClick={() => bulk.toggleAll(ids)}
                className="px-3 py-2 text-[10px] uppercase tracking-widest border border-[#1a4640] rounded-sm hover:border-[#d4af37]/50"
                data-testid="assign-toggle-all">
          {bulk.allSelected ? "Desselecionar tudo" : "Selecionar tudo"}
        </button>
        <button onClick={assign} disabled={saving || bulk.count === 0}
                className="btn-gold px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                data-testid="assign-submit">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Atribuir a {bulk.count} cliente{bulk.count === 1 ? "" : "s"}
        </button>
      </div>

      {/* Mobile: stacked tappable cards */}
      <div className="md:hidden space-y-2" data-testid="assign-list-mobile">
        {filtered.length === 0 && (
          <div className="lux-card p-6 text-center text-sm text-[#a0b2af]">Sem clientes.</div>
        )}
        {filtered.map((c) => {
          const sel = bulk.isSelected(c.id);
          return (
            <button key={c.id}
                    type="button"
                    onClick={() => bulk.toggle(c.id)}
                    className={`w-full text-left lux-card p-4 flex items-start gap-3 transition-all ${
                      sel ? "ring-2 ring-[#d4af37] bg-[#d4af37]/5" : "hover:border-[#d4af37]/40"
                    }`}
                    data-testid={`assign-row-mobile-${c.id}`}>
              <BulkCheckbox id={c.id} checked={sel}
                            onChange={() => bulk.toggle(c.id)} />
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[10px] text-[#d4af37]">{c.registration_number || "—"}</div>
                <div className="text-sm text-[#fdfbf7] mt-0.5">{c.name}</div>
                <div className="text-[11px] text-[#a0b2af] break-all">{c.email}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block lux-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#0a3a33] text-[#a0b2af] text-xs uppercase tracking-widest">
            <tr>
              <th className="p-3 w-10">
                <BulkCheckbox checked={bulk.allSelected} indeterminate={bulk.someSelected}
                              onChange={() => bulk.toggleAll(ids)} />
              </th>
              <th className="text-left p-3">Nº Reg.</th>
              <th className="text-left p-3">Nome</th>
              <th className="text-left p-3">Email</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-[#a0b2af]">Sem clientes.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id}
                  className={`border-t border-[#1a4640] cursor-pointer ${bulk.isSelected(c.id) ? "bg-[#d4af37]/5" : ""}`}
                  onClick={() => bulk.toggle(c.id)}
                  data-testid={`assign-row-${c.id}`}>
                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                  <BulkCheckbox id={c.id} checked={bulk.isSelected(c.id)}
                                onChange={() => bulk.toggle(c.id)} />
                </td>
                <td className="p-3 font-mono text-xs text-[#d4af37]">{c.registration_number || "—"}</td>
                <td className="p-3">{c.name}</td>
                <td className="p-3 text-[#a0b2af]">{c.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- List panes

function ListPane({ status }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/promos?status=${status}`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  async function revoke(pid) {
    if (!window.confirm("Revogar esta promoção? Deixa de estar disponível para o cliente.")) return;
    try {
      await api.delete(`/admin/promos/${pid}`);
      toast.success("Revogada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (loading) return <div className="text-sm text-[#a0b2af]">A carregar…</div>;
  if (items.length === 0) return (
    <div className="lux-card p-10 text-center text-sm text-[#a0b2af]">
      Sem promoções nesta categoria.
    </div>
  );

  return (
    <div className="space-y-3" data-testid={`list-${status}`}>
      {items.map((p) => (
        <div key={p.id} className="lux-card p-5" data-testid={`promo-${p.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#d4af37] flex items-center gap-2 flex-wrap">
                <SourceBadge source={p.source} />
                {p.client_reg && (
                  <span className="font-mono normal-case tracking-normal text-[#a0b2af]">· {p.client_reg}</span>
                )}
              </div>
              <div className="mt-2 text-base text-[#fdfbf7]">{p.client_name || "—"}
                <span className="text-xs text-[#a0b2af] ml-2">{p.client_email}</span>
              </div>
              <div className="text-xs text-[#a0b2af] mt-1">
                <Calendar size={10} className="inline mr-1" />
                {p.starts_at?.slice(0, 10)} → {p.expires_at?.slice(0, 10)}
                {p.redeemed_at && <span className="ml-2 text-[#10b981]">· usada em {p.redeemed_at.slice(0, 10)}</span>}
                {p.note && <span className="ml-2 italic">· {p.note}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <div className="font-display text-2xl text-[#d4af37]">
                  {p.percentage > 0
                    ? `${p.percentage}%`
                    : `${Number(p.amount_eur || 0).toFixed(2)} €`}
                </div>
              </div>
              {status === "active" && (
                <button onClick={() => revoke(p.id)}
                        className="text-[#fca5a5] hover:text-[#ef4444] p-2"
                        title="Revogar"
                        data-testid={`revoke-${p.id}`}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SourceBadge({ source }) {
  const map = {
    welcome: { label: "Boas-vindas", color: "#d4af37" },
    welcome_referred: { label: "Boas-vindas (indicado)", color: "#10b981" },
    referral: { label: "Indicação", color: "#3b82f6" },
    manual: { label: "Manual", color: "#f59e0b" },
  };
  const s = map[source] || { label: source, color: "#a0b2af" };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm"
          style={{ borderColor: s.color, color: s.color }}>
      <Sparkles size={9} /> {s.label}
    </span>
  );
}
